import React from 'react';
import { colors, PopOver } from '@insurancemenu/imenu-components-lib';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  RenewalActionType,
  RENEWAL_ACTION_DISABLED_REASON_LABEL,
  RenewalActionMap,
} from 'store/modules/initiate-quote/types';
import { observer } from 'mobx-react-lite';
import { InfoButtonFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { AccessTag } from 'components/common/general/AccessTag';
import { INITIATE_PROPOSAL_RENEWAL_ACTION_MORE_PRODUCT_LINES_TEST_ID } from '../constants';
import {
  OptionContentStyled,
  OptionTitleStyled,
  OptionDescriptionStyled,
  DisabledReasonBlockStyled,
  DisabledReasonTextStyled,
  RenewalActionOptionStyled,
  ProductLinesBlockStyled,
  ProductLineTitleStyled,
  ProductLinesPopoverListStyled,
  ProductLinesPopoverItemStyled,
  MoreProductLinesButtonStyled,
  StatusColumnStyled,
  StatusLabelStyled,
  PopoverProductLinesLabelStyled,
} from './styles';

interface Props {
  renewalAction: RenewalActionMap;
}

interface ProductLinesProps {
  productLineIds: ProductLineId[];
}

const RENEWAL_ACTION_COPY: Record<RenewalActionType, { label: string; description: string }> = {
  ADD_BENEFIT: {
    label: 'Add Benefit',
    description: 'Add an additional benefit to your proposal',
  },
  RENEW_AS_IS: {
    label: 'Renew as is',
    description: 'Renew your current plan without changes',
  },
  CANCEL_COVERAGE: {
    label: 'Cancel Coverage',
    description: 'Cancel one or more product lines from this renewal',
  },
  CHANGE_PLAN: {
    label: 'Change Plan',
    description: 'Change your current plan',
  },
};

const VISIBLE_PRODUCT_LINES_LIMIT = 2;

const ProductLines: React.FC<ProductLinesProps> = ({ productLineIds }) => {
  if (productLineIds.length === 0) {
    return null;
  }

  const visibleProductLineIds = productLineIds.slice(0, VISIBLE_PRODUCT_LINES_LIMIT);
  const remainingCount = productLineIds.length - VISIBLE_PRODUCT_LINES_LIMIT;

  return (
    <ProductLinesBlockStyled>
      {visibleProductLineIds.map((productLineId) => (
        <ProductLineTitleStyled key={productLineId} productLineId={productLineId} iconSize="1.6rem" />
      ))}
      {remainingCount > 0 && (
        <div>
          <PopOver
            alignment="bottomEnd"
            data-test-id={INITIATE_PROPOSAL_RENEWAL_ACTION_MORE_PRODUCT_LINES_TEST_ID}
            content={
              <div>
                <PopoverProductLinesLabelStyled>Available product lines:</PopoverProductLinesLabelStyled>
                <ProductLinesPopoverListStyled>
                  {productLineIds.map((productLineId) => (
                    <ProductLinesPopoverItemStyled key={productLineId}>
                      <ProductLineTitleStyled productLineId={productLineId} iconSize="1.6rem" />
                    </ProductLinesPopoverItemStyled>
                  ))}
                </ProductLinesPopoverListStyled>
              </div>
            }
          >
            <MoreProductLinesButtonStyled uppercase type="text" size="small" color="primary">
              + {remainingCount} more
            </MoreProductLinesButtonStyled>
          </PopOver>
        </div>
      )}
    </ProductLinesBlockStyled>
  );
};

const RenewalAction: React.FC<Props> = observer(
  ({ renewalAction: { action, disabledReason, productLineIds, accessLimitation } }) => {
    const { label, description } = RENEWAL_ACTION_COPY[action];
    const isDisabled = Boolean(disabledReason);

    return (
      <RenewalActionOptionStyled $disabled={isDisabled}>
        <OptionContentStyled>
          <OptionTitleStyled>
            {label} {accessLimitation && <AccessTag size="small" accessLimitation={accessLimitation} />}
          </OptionTitleStyled>
          <OptionDescriptionStyled>{description}</OptionDescriptionStyled>
        </OptionContentStyled>

        <StatusColumnStyled>
          <StatusLabelStyled $disabled={isDisabled}>{isDisabled ? 'Unavailable' : 'Available'}</StatusLabelStyled>
          {disabledReason ? (
            <DisabledReasonBlockStyled>
              <InfoButtonFilled width="2rem" height="2rem" color={colors.indigo[600]} />
              <DisabledReasonTextStyled>
                {RENEWAL_ACTION_DISABLED_REASON_LABEL[disabledReason]}
              </DisabledReasonTextStyled>
            </DisabledReasonBlockStyled>
          ) : (
            <ProductLines productLineIds={productLineIds} />
          )}
        </StatusColumnStyled>
      </RenewalActionOptionStyled>
    );
  },
);

export default RenewalAction;
